import React from "react";
import { useLang } from "@/lib/useLang";
import { getCandidateName } from "@/lib/i18n";

const COLOR_MAP = {
  saffron: "#FF9933",
  green: "#138808",
  red: "#E3000F",
};

/**
 * Two stacked (side-by-side on desktop) cards:
 *  - LEFT: top 3 candidates by % from the citizen poll
 *  - RIGHT: the officially announced CM (photo + name + date)
 *
 * Shown only when poll is frozen.
 */
export const PublicPulseVsOfficial = ({ results, pollState }) => {
  const { t, lang } = useLang();
  if (!pollState?.frozen || !results) return null;

  const sorted = [...results.candidates].sort((a, b) => b.votes - a.votes);
  const officialName = pollState.announced_cm_name || "—";
  const officialPhoto = pollState.announced_cm_photo || "";
  const officialAt = pollState.announced_at
    ? new Date(pollState.announced_at).toLocaleDateString(
        lang === "ml" ? "ml-IN" : "en-IN",
        { year: "numeric", month: "long", day: "numeric" },
      )
    : "";

  return (
    <section className="space-y-6 fade-up" data-testid="comparison-section">
      <h2
        className="font-display uppercase leading-none"
        style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}
        data-testid="comparison-heading"
      >
        {t("comparison_heading")}
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
        {/* LEFT — public pulse */}
        <div className="brutal-card p-6 md:p-8" data-testid="comparison-public-card">
          <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600 mb-2">
            {t("comparison_public_label")}
          </p>
          <p
            className="font-display uppercase leading-none mb-6"
            style={{ fontSize: "1.5rem" }}
          >
            {t("final_results_label")} ·{" "}
            <span data-testid="comparison-public-total">
              {results.total_votes.toLocaleString()}
            </span>{" "}
            {t("votes_word")}
          </p>

          <div className="space-y-5">
            {sorted.map((c, i) => {
              const color = COLOR_MAP[c.color];
              const isTop = i === 0 && results.total_votes > 0;
              return (
                <div key={c.id} data-testid={`comparison-row-${c.id}`}>
                  <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="border-4 border-black overflow-hidden flex items-center justify-center font-display shrink-0"
                        style={{
                          background: color,
                          color: c.color === "saffron" ? "#0A0A0A" : "#FFFFFF",
                          width: 44,
                          height: 44,
                          fontSize: "1.1rem",
                          lineHeight: 1,
                        }}
                        aria-hidden="true"
                      >
                        {c.photo ? (
                          <img
                            src={c.photo}
                            alt=""
                            crossOrigin="anonymous"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              objectPosition: "50% 18%",
                            }}
                          />
                        ) : (
                          c.initials
                        )}
                      </div>
                      <h3
                        className="font-display uppercase leading-none truncate"
                        style={{ fontSize: "1.25rem" }}
                      >
                        {getCandidateName(c, lang)}
                      </h3>
                      {isTop && (
                        <span
                          className="text-[10px] uppercase font-extrabold tracking-[0.25em] px-2 py-0.5 border-2 border-black shrink-0"
                          style={{ background: "#0A0A0A", color: "#FF9933" }}
                          data-testid={`comparison-top-tag-${c.id}`}
                        >
                          ★ TOP
                        </span>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p
                        className="font-display leading-none"
                        style={{ fontSize: "1.75rem" }}
                        data-testid={`comparison-pct-${c.id}`}
                      >
                        {c.percentage.toFixed(1)}%
                      </p>
                      <p
                        className="text-[10px] md:text-xs uppercase font-bold tracking-[0.2em] text-neutral-600 mt-1"
                        data-testid={`comparison-votes-${c.id}`}
                      >
                        {c.votes.toLocaleString()} {t("votes_word")}
                      </p>
                    </div>
                  </div>
                  <div className="border-2 border-black h-5 bg-neutral-100 overflow-hidden">
                    <div
                      className="h-full"
                      style={{
                        width: `${Math.max(c.percentage, 0)}%`,
                        background: color,
                        transition: "width 600ms ease-out",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <p
            className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600 mt-6"
            data-testid="poll-locked-note"
          >
            {t("poll_locked_note")}
          </p>
        </div>

        {/* RIGHT — official decision */}
        <div
          className="brutal-card p-6 md:p-8 flex flex-col"
          style={{ background: "#FF9933" }}
          data-testid="comparison-official-card"
        >
          <p
            className="text-xs uppercase font-bold tracking-[0.3em] mb-2"
            style={{ color: "#0A0A0A" }}
          >
            {t("comparison_official_label")}
          </p>
          <p
            className="font-display uppercase leading-none mb-6"
            style={{ fontSize: "1.5rem", color: "#0A0A0A" }}
          >
            {t("announcement_label")}
          </p>

          <div className="flex-1 flex flex-col items-center justify-center text-center gap-5">
            {officialPhoto ? (
              <div
                className="border-4 border-black overflow-hidden"
                style={{ width: 200, height: 200, background: "#0A0A0A" }}
                data-testid="comparison-official-photo"
              >
                <img
                  src={officialPhoto}
                  alt={officialName}
                  crossOrigin="anonymous"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "50% 18%",
                  }}
                />
              </div>
            ) : (
              <div
                className="border-4 border-black flex items-center justify-center font-display"
                style={{
                  width: 200,
                  height: 200,
                  background: "#0A0A0A",
                  color: "#FF9933",
                  fontSize: "5rem",
                  lineHeight: 1,
                }}
              >
                CM
              </div>
            )}

            <h3
              className="font-display uppercase leading-[0.9] tracking-tight"
              style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)", color: "#0A0A0A" }}
              data-testid="comparison-official-name"
            >
              {officialName}
            </h3>

            {officialAt && (
              <p
                className="text-xs md:text-sm uppercase font-bold tracking-[0.25em]"
                style={{ color: "#0A0A0A" }}
                data-testid="comparison-official-date"
              >
                {t("announced_on")}: {officialAt}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PublicPulseVsOfficial;
